import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import WatchFilledIcon from '@atlaskit/icon/core/eye-open-filled';
import WatchIcon from '@atlaskit/icon/core/eye-open';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { ShortcutVisualizer } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	UNIVERSAL_WATCH_CONTENT_SHORTCUT,
	UNIVERSAL_WATCH_CONTENT_SHORTCUT_SAFARI,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { isSafari } from '@confluence/browser-helper';

const i18n = defineMessages({
	startWatchingIconLabel: {
		id: 'watch-dialog.watch-button.start.watching.label',
		defaultMessage: 'Start watching',
		description: 'A label for the start watching space/page button',
	},
	stopWatchingIconLabel: {
		id: 'watch-dialog.watch-button.stop.watching.label',
		defaultMessage: 'Stop watching',
		description: 'A label for the stop watching space/page button',
	},
	watchStopTooltip: {
		id: 'watch-dialog.stop.button.tooltip',
		defaultMessage: 'Stop watching',
		description: 'Stop watching tooltip',
	},
	watchStartTooltip: {
		id: 'watch-dialog.start.button.tooltip',
		defaultMessage: 'Watch',
		description: 'Start Watching tooltip',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const StyledButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		minWidth: '30px',
	},
});

const WatchButtonComponent = ({
	isDisabled,
	isWatching,
	isSelected,
	onClick,
	onMouseOver,
	intl,
	triggerProps,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const fireClickAnalyticsEvent = useCallback(() => {
		const analyticsData = {
			action: 'clicked',
			actionSubject: 'content-watch-button',
			source: 'viewPageScreen',
		};

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: analyticsData,
		}).fire();
	}, [createAnalyticsEvent]);

	const handleWatchButtonClick = () => {
		onClick();
		fireClickAnalyticsEvent();
	};

	const shortcutContent = isWatching
		? intl.formatMessage(i18n.watchStopTooltip)
		: intl.formatMessage(i18n.watchStartTooltip);

	const getTooltipContent = () => {
		if (isDisabled) {
			return undefined;
		}
		if (isLiveEditMode || isOnEditRoute) {
			return shortcutContent;
		}

		return (
			<ShortcutVisualizer
				shortcut={
					isSafari() ? UNIVERSAL_WATCH_CONTENT_SHORTCUT_SAFARI : UNIVERSAL_WATCH_CONTENT_SHORTCUT
				}
				contentBefore={shortcutContent}
				isTooltip
			/>
		);
	};

	return (
		<Tooltip tag="span" content={getTooltipContent()}>
			<StyledButton
				data-id="page-watch-button"
				data-test-id="page-watch-button"
				testId="page-watch-button"
				appearance="subtle"
				isDisabled={isDisabled}
				isSelected={isSelected}
				onClick={handleWatchButtonClick}
				onMouseOver={onMouseOver}
				onFocus={onMouseOver}
				iconBefore={
					isWatching ? (
						<WatchFilledIcon
							label={intl.formatMessage(i18n.stopWatchingIconLabel)}
							testId="watch-filled-icon"
							spacing="spacious"
						/>
					) : (
						<WatchIcon
							label={intl.formatMessage(i18n.startWatchingIconLabel)}
							testId="watch-icon"
							spacing="spacious"
						/>
					)
				}
				{...triggerProps}
			/>
		</Tooltip>
	);
};

WatchButtonComponent.propTypes = {
	isDisabled: PropTypes.bool,
	isWatching: PropTypes.bool.isRequired,
	isSelected: PropTypes.bool.isRequired,
	onClick: PropTypes.func.isRequired,
	onMouseOver: PropTypes.func,
	intl: PropTypes.object,
	triggerProps: PropTypes.object,
};

export const WatchButton = injectIntl(WatchButtonComponent);
